/**
 * Auswertung der Auditsätze ohne React (REQ-26 §2.1) — Port von `audit-diff.ts` der ChinaApp.
 *
 * Was die API liefert (`SeefrachtContext.OnBeforeSaveChanges`, `Helper/AuditEntry.cs`):
 *
 * - `OldValues`/`NewValues` sind `JsonSerializer.Serialize(Dictionary<string, object>)`. Die
 *   Schlüssel sind die C#-Eigenschaftsnamen und damit dieselbe Schreibweise wie
 *   `ChinaAppSysViewField.FieldName`; Aufzählungen stehen als Zahl darin.
 * - `AffectedColumns` ist `JsonSerializer.Serialize(List<string>)` und nur bei einer Änderung
 *   gefüllt. Beim Anlegen steht alles in `NewValues`, beim Löschen alles in `OldValues`.
 *
 * Der Bestand ist nie migriert worden. Ein Satz mit unlesbarem JSON darf die Schublade nicht
 * zerlegen; er zeigt dann seinen Rohtext — so viel, wie die alte Tabelle ohnehin zeigte.
 */

import { getCaption } from '@/types/captions';
import type { ChinaAppSysAudit, ChinaAppSysViewField } from '@/types/entities';
import {
  EntityTypes,
  rechnungArtAufteilungDisplayNames,
  rechnungsartDisplayNames,
} from '@/types/enums';
import { getFieldTypes, type ColumnValueType } from '@/types/fieldTypes';
import { formatDate, formatDateTime, parseApiDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';

/** Was an der Stelle eines fehlenden Wertes steht. */
export const EMPTY_VALUE = '—';

// =============================================================================
// Art der Änderung
// =============================================================================

export type AuditKind = 'created' | 'updated' | 'deleted' | 'unknown';

export type AuditKindFilter = AuditKind | 'all';

/**
 * Die API schreibt `AuditType.ToString()`, also `Create`, `Update`, `Delete`. Gelesen wird
 * auch die Schreibweise des `EntityState`, aus dem der Server die Art ableitet.
 */
const KIND_BY_TYPE: Readonly<Record<string, AuditKind>> = {
  create: 'created',
  added: 'created',
  update: 'updated',
  modified: 'updated',
  delete: 'deleted',
  deleted: 'deleted',
};

export const auditKindLabels: Readonly<Record<AuditKind, string>> = {
  created: 'Angelegt',
  updated: 'Geändert',
  deleted: 'Gelöscht',
  unknown: 'Unbekannt',
};

export function toAuditKind(type: string | null | undefined): AuditKind {
  if (type === null || type === undefined) return 'unknown';
  return KIND_BY_TYPE[type.trim().toLowerCase()] ?? 'unknown';
}

// =============================================================================
// Werte lesen
// =============================================================================

export interface ParsedAuditValues {
  /** Die gelesene Abbildung Feldname → Wert, oder `null`. */
  values: Record<string, unknown> | null;
  /** Der Rohtext, wenn die Zeichenkette kein JSON-Objekt ist. */
  unreadable: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Liest eine der beiden Wertelisten. Wirft nie: Unlesbares kommt als Rohtext zurück. */
export function parseAuditValues(text: string | null | undefined): ParsedAuditValues {
  if (text === null || text === undefined || text.trim() === '') {
    return { values: null, unreadable: null };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { values: null, unreadable: text };
  }
  return isRecord(parsed)
    ? { values: parsed, unreadable: null }
    : { values: null, unreadable: text };
}

/**
 * Liest `AffectedColumns`. Der Regelfall ist eine JSON-Liste; die Kommaliste ist der Rückfall
 * für Sätze, die anders geschrieben wurden.
 */
export function parseAffectedColumns(text: string | null | undefined): string[] {
  if (text === null || text === undefined || text.trim() === '') return [];

  try {
    const parsed: unknown = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (item): item is string => typeof item === 'string' && item.trim() !== '',
      );
    }
  } catch {
    // Fällt auf die Kommaliste zurück.
  }

  return text
    .split(',')
    .map((part) => part.replace(/["[\]]/g, '').trim())
    .filter((part) => part !== '');
}

// =============================================================================
// Werte darstellen
// =============================================================================

/** Anzeigenamen einer Aufzählung, nach Zahlwert. */
export type DisplayNames = Readonly<Record<number, string>>;

/**
 * Felder mit Aufzählungstyp. Die Feldtyp-Tabellen führen sie als `'string'`, weil die
 * Filterzeile sie als Text filtert; im Audit stehen sie als Zahl.
 */
const ENUM_FIELDS: Partial<Record<EntityTypes, Readonly<Record<string, DisplayNames>>>> = {
  [EntityTypes.Rechnung]: {
    Rechnungsart: rechnungsartDisplayNames,
    ArtAufteilung: rechnungArtAufteilungDisplayNames,
  },
  [EntityTypes.Rechnungszuordnung]: {
    Rechnung_Rechnungsart: rechnungsartDisplayNames,
    Rechnung_ArtAufteilung: rechnungArtAufteilungDisplayNames,
  },
};

/** Zeitstempel der API. Was nicht so beginnt, ist kein Datum — auch wenn `Date` es läse. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

/** Ein Wert, wie er im JSON steht; Zeichenketten ohne Anführungszeichen. */
function plainText(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

/** `dd.MM.yyyy`, mit Uhrzeit nur, wenn sie nicht 00:00 ist; `null`, wenn es kein Datum ist. */
function formatAuditDate(value: unknown): string | null {
  if (typeof value !== 'string' || !ISO_DATE.test(value.trim())) return null;
  const date = parseApiDate(value);
  if (date === null) return null;
  return date.getHours() === 0 && date.getMinutes() === 0 ? formatDate(date) : formatDateTime(date);
}

function formatAuditBoolean(value: unknown): string | null {
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
  const lower = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (lower === 'true') return 'Ja';
  if (lower === 'false') return 'Nein';
  return null;
}

/**
 * Ein Wert in der Schreibweise seines Feldtyps. Passt der Wert nicht zum Typ, wird er als Text
 * gezeigt statt verschluckt; ein unbekannter Typ (`undefined`) zeigt ihn, wie er im JSON steht.
 */
export function formatAuditValue(
  value: unknown,
  type: ColumnValueType | undefined,
  displayNames?: DisplayNames,
): string {
  if (value === null || value === undefined) return EMPTY_VALUE;
  if (typeof value === 'string' && value.trim() === '') return EMPTY_VALUE;

  if (displayNames !== undefined) {
    return typeof value === 'number' ? (displayNames[value] ?? String(value)) : plainText(value);
  }

  switch (type) {
    case 'date':
      return formatAuditDate(value) ?? plainText(value);
    case 'boolean':
      return formatAuditBoolean(value) ?? plainText(value);
    case 'integer':
    case 'number':
      if (typeof value !== 'number' || !Number.isFinite(value)) return plainText(value);
      return formatNumber(value, type === 'integer' ? 'N0' : 'N2');
    default:
      return plainText(value);
  }
}

// =============================================================================
// Die Felder
// =============================================================================

export interface AuditFieldInfo {
  label: string;
  /** `undefined`, wenn die Feldtyp-Tabelle der Entität das Feld nicht kennt. */
  type: ColumnValueType | undefined;
  /** Gesetzt, wenn das Feld eine Aufzählung ist. */
  displayNames: DisplayNames | undefined;
}

export type AuditFieldResolver = (fieldName: string) => AuditFieldInfo;

/**
 * Beschriftung und Typ eines Feldes. Die Beschriftung ist dieselbe wie im Spaltenkopf des
 * Grids: `FieldCaption` der View, dann `getCaption`, sonst der Feldname — die Rangfolge aus
 * `getColumnHeader` in `components/grid/columnsFromViewFields.ts`, die dort nicht exportiert
 * ist. Felder, die die View nicht führt, werden nicht verworfen (REQ-042 der ChinaApp): Sie
 * stehen mit ihrer Caption bzw. ihrem Feldnamen da. Auch `Visible: false` behält seine
 * `FieldCaption` — ein Feld, das ohnehin erscheint, wäre unter seinem technischen Namen nur
 * schlechter lesbar.
 */
export function createAuditFieldResolver(
  entity: EntityTypes,
  fields: readonly ChinaAppSysViewField[],
): AuditFieldResolver {
  const captions = new Map<string, string>();
  for (const field of fields) {
    const caption = field.FieldCaption;
    if (field.FieldName !== null && caption !== null && caption !== '') {
      captions.set(field.FieldName, caption);
    }
  }
  const types = getFieldTypes(entity);
  const enums = ENUM_FIELDS[entity] ?? {};

  return (fieldName) => ({
    label: captions.get(fieldName) ?? getCaption(entity, fieldName),
    type: types[fieldName],
    displayNames: enums[fieldName],
  });
}

// =============================================================================
// Der Auditsatz
// =============================================================================

export interface AuditChange {
  /** Technischer Feldname — zugleich Schlüssel im JSON. */
  field: string;
  label: string;
  oldText: string;
  newText: string;
  /** Stand das Feld in `OldValues`? Beim Anlegen gibt es kein Vorher. */
  hasOld: boolean;
  /** Stand das Feld in `NewValues`? Beim Löschen gibt es kein Nachher. */
  hasNew: boolean;
}

export interface AuditRawText {
  affectedColumns: string | null;
  oldValues: string | null;
  newValues: string | null;
}

export interface AuditRecord {
  id: number;
  kind: AuditKind;
  kindLabel: string;
  timestamp: Date | null;
  /** `dd.MM.yyyy HH:mm`; bei unlesbarem Zeitpunkt der Rohwert. */
  timestampText: string;
  timestampIso: string;
  username: string;
  changes: AuditChange[];
  /** Rohtext, wenn eine der beiden Wertelisten kein JSON-Objekt ist. */
  unreadable: { oldValues: string | null; newValues: string | null } | null;
  raw: AuditRawText;
  /** Kleingeschrieben, für die Suche. */
  searchText: string;
}

/**
 * `AffectedColumns` gibt die Reihenfolge vor. Schlüssel, die dort fehlen, aber in einer der
 * Wertelisten stehen, werden hinten angehängt statt verworfen — beim Anlegen und Löschen ist
 * die Liste immer leer.
 */
function orderedKeys(
  affected: readonly string[],
  oldValues: Record<string, unknown> | null,
  newValues: Record<string, unknown> | null,
): string[] {
  const keys = new Set(affected);
  for (const values of [oldValues, newValues]) {
    if (values === null) continue;
    for (const key of Object.keys(values)) keys.add(key);
  }
  return [...keys];
}

function toRecord(entry: ChinaAppSysAudit, describe: AuditFieldResolver): AuditRecord {
  const oldSide = parseAuditValues(entry.OldValues);
  const newSide = parseAuditValues(entry.NewValues);
  const keys = orderedKeys(
    parseAffectedColumns(entry.AffectedColumns),
    oldSide.values,
    newSide.values,
  );

  const changes: AuditChange[] = [];
  for (const key of keys) {
    const hasOld = oldSide.values !== null && Object.hasOwn(oldSide.values, key);
    const hasNew = newSide.values !== null && Object.hasOwn(newSide.values, key);
    // Ein Name aus `AffectedColumns` ohne Wert auf beiden Seiten sagt nichts — das kommt vor,
    // wenn deren JSON unlesbar ist. Dann ist der Rohtext die Auskunft, keine Zeile voller „—".
    if (!hasOld && !hasNew) continue;

    const info = describe(key);
    changes.push({
      field: key,
      label: info.label,
      oldText: hasOld ? formatAuditValue(oldSide.values?.[key], info.type, info.displayNames) : '',
      newText: hasNew ? formatAuditValue(newSide.values?.[key], info.type, info.displayNames) : '',
      hasOld,
      hasNew,
    });
  }

  const kind = toAuditKind(entry.Type);
  const timestamp = parseApiDate(entry.DateTime);
  const formatted = formatDateTime(timestamp);
  const username =
    entry.Username === null || entry.Username.trim() === '' ? EMPTY_VALUE : entry.Username;
  const unreadable =
    oldSide.unreadable === null && newSide.unreadable === null
      ? null
      : { oldValues: oldSide.unreadable, newValues: newSide.unreadable };

  const searchParts = [username, auditKindLabels[kind], formatted];
  for (const change of changes) {
    searchParts.push(change.field, change.label, change.oldText, change.newText);
  }
  if (unreadable !== null) {
    searchParts.push(unreadable.oldValues ?? '', unreadable.newValues ?? '');
  }

  return {
    id: entry.Id,
    kind,
    kindLabel: auditKindLabels[kind],
    timestamp,
    timestampText: formatted === '' ? entry.DateTime : formatted,
    timestampIso: entry.DateTime,
    username,
    changes,
    unreadable,
    raw: {
      affectedColumns: entry.AffectedColumns,
      oldValues: entry.OldValues,
      newValues: entry.NewValues,
    },
    searchText: searchParts.join(' ').toLowerCase(),
  };
}

/** Ein einzelner Auditsatz; für eine Liste ist `parseAuditEntries` der richtige Weg. */
export function parseAuditEntry(
  entry: ChinaAppSysAudit,
  entity: EntityTypes,
  fields: readonly ChinaAppSysViewField[],
): AuditRecord {
  return toRecord(entry, createAuditFieldResolver(entity, fields));
}

/** Der ganze Verlauf, neueste zuerst; bei gleichem Zeitpunkt der jüngere Satz vorn. */
export function parseAuditEntries(
  entries: readonly ChinaAppSysAudit[],
  entity: EntityTypes,
  fields: readonly ChinaAppSysViewField[],
): AuditRecord[] {
  const describe = createAuditFieldResolver(entity, fields);
  return entries
    .map((entry) => toRecord(entry, describe))
    .sort((left, right) => {
      const difference = (right.timestamp?.getTime() ?? 0) - (left.timestamp?.getTime() ?? 0);
      return difference !== 0 ? difference : right.id - left.id;
    });
}

// =============================================================================
// Filter
// =============================================================================

export interface AuditFilterState {
  search: string;
  kind: AuditKindFilter;
  from: Date | null;
  to: Date | null;
}

export const emptyAuditFilter: AuditFilterState = { search: '', kind: 'all', from: null, to: null };

export function isAuditFilterActive(filter: AuditFilterState): boolean {
  return (
    filter.search.trim() !== '' ||
    filter.kind !== 'all' ||
    filter.from !== null ||
    filter.to !== null
  );
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function endOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).getTime() - 1;
}

/**
 * Alle Wortteile der Suche müssen vorkommen — sonst fände „menge 5" nichts, sobald die beiden
 * Angaben in verschiedenen Zeilen der Karte stehen. Der Zeitraum schließt beide Grenztage ein.
 */
export function filterAuditRecords(
  records: readonly AuditRecord[],
  filter: AuditFilterState,
): AuditRecord[] {
  const needles = filter.search
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((part) => part !== '');
  const from = filter.from === null ? null : startOfDay(filter.from);
  const to = filter.to === null ? null : endOfDay(filter.to);

  return records.filter((record) => {
    if (filter.kind !== 'all' && record.kind !== filter.kind) return false;

    if (from !== null || to !== null) {
      const time = record.timestamp?.getTime();
      if (time === undefined) return false;
      if (from !== null && time < from) return false;
      if (to !== null && time > to) return false;
    }

    return needles.every((needle) => record.searchText.includes(needle));
  });
}
